import useTheme from '@hero/branding/theme';
import Modal from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import React from 'react';
import QueryInlineError from '../../../../components/QueryInlineError';
import InlineLoader from '../../../../components/InlineLoader';
import ToLocalTime from '../../../../utils/toLocalTime';
import useGenerateWebUIPassword from '../api/useGenerateWebUIPassword';

interface WebUIPasswordProps {
    deviceId: number;
    serial: string | null;
}

const WebUIPassword: React.FC<WebUIPasswordProps> = ({ deviceId, serial }) => {
    const theme = useTheme();
    const [showModal, setShowModal] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const { mutate, data, isPending, isError, error, reset } = useGenerateWebUIPassword();

    const generate = React.useCallback(() => {
        setCopied(false);
        mutate(deviceId);
    }, [mutate, deviceId]);

    const handleOpen = React.useCallback(() => {
        setShowModal(true);
        generate();
    }, [generate]);

    const handleClose = React.useCallback(() => {
        setShowModal(false);
        setCopied(false);
        // clear the plaintext from memory once the modal is dismissed (shown-once)
        reset();
    }, [reset]);

    const handleCopy = React.useCallback(() => {
        if (!data?.password) {
            return;
        }
        navigator.clipboard.writeText(data.password).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [data]);

    const passwordBoxStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: '1.2rem',
        marginTop: '1.6rem',
        marginBottom: '1.2rem'
    };

    const passwordTextStyle: React.CSSProperties = {
        fontFamily: 'monospace',
        fontSize: '3.2rem',
        fontWeight: 'bold',
        letterSpacing: '0.4rem',
        color: theme.colors.brand.primary.alpha,
        background: '#F4F5F7',
        borderRadius: '0.6rem',
        padding: '1rem 1.6rem'
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', height: '2rem' }}>
                <Button size="small" onClick={handleOpen} variant="outlineSecondary">
                    View Password
                </Button>
            </div>

            <Modal
                type={data ? 'success' : 'neutral'}
                onClose={handleClose}
                isCancelable
                externalControls={[showModal, setShowModal]}
            >
                <H role="h4" centered>
                    WebUI / UART Password
                </H>

                {isPending ? (
                    <InlineLoader styles={{ padding: '4rem' }} />
                ) : isError ? (
                    <Section centered noDefaultPadding>
                        <QueryInlineError centered error={error} />
                        <Button onClick={generate} width="large">
                            Try again
                        </Button>
                        <Button variant="secondary" onClick={handleClose} width="large">
                            Close
                        </Button>
                    </Section>
                ) : data ? (
                    <Section centered noDefaultPadding>
                        <div style={passwordBoxStyle}>
                            <span style={passwordTextStyle}>{data.password}</span>
                            <Button size="small" variant="secondary" onClick={handleCopy}>
                                {copied ? 'Copied!' : 'Copy'}
                            </Button>
                        </div>

                        <P centered noDefaultMargin>
                            <strong>Username:</strong> {serial || '-'}
                        </P>
                        <P centered noDefaultMargin>
                            <strong>Expires:</strong>{' '}
                            {ToLocalTime(data.expires_at, 'YYYY-MM-DD HH:mm') || '-'}
                        </P>

                        <P centered size="small">
                            Shown once. To see it again, click Regenerate — this overwrites the
                            previous password.
                        </P>

                        <Button onClick={generate} width="large">
                            Regenerate
                        </Button>
                        <Button variant="secondary" onClick={handleClose} width="large">
                            Done
                        </Button>
                    </Section>
                ) : null}
            </Modal>
        </>
    );
};

export default WebUIPassword;
